/**
 * Local SQLite adapters for CMS data.
 * Mirrors the Convex api.cmsData.* interface.
 */
import { localDb, newId, now } from "@/lib/localDb";
import { invalidate } from "./queryCache";
import { useLocalQuery, useLocalGet } from "./useLocalQuery";

export interface LocalCmsData {
  _id: string;
  project_id: string;
  name: string;
  description?: string;
  original_filename?: string;
  json_file_path?: string;
  field_structure?: unknown;
  row_count?: number;
  field_count?: number;
  import_errors?: unknown[];
  file_size?: number;
  created_at?: string;
  updated_at?: string;
}

export function useCmsDataByProject(projectId: string | undefined, craftspaceId: string | undefined): LocalCmsData[] | undefined {
  return useLocalQuery<LocalCmsData>(
    "SELECT id as _id, * FROM cms_data WHERE project_id = ? ORDER BY created_at ASC",
    [projectId ?? ""],
    ["cms_data"],
    ["field_structure", "import_errors"],
    craftspaceId,
  );
}

export function useCmsDataItem(id: string | undefined, craftspaceId: string | undefined): LocalCmsData | null | undefined {
  return useLocalGet<LocalCmsData>(
    "SELECT id as _id, * FROM cms_data WHERE id = ?",
    [id ?? ""],
    ["cms_data"],
    ["field_structure", "import_errors"],
    craftspaceId,
  );
}

export async function createCmsData(params: {
  craftspace_id: string;
  project_id: string;
  name: string;
  description?: string;
  original_filename?: string;
  json_file_path?: string;
  field_structure?: unknown;
  row_count?: number;
  field_count?: number;
  import_errors?: unknown[];
  file_size?: number;
}): Promise<string> {
  const id = newId();
  const ts = now();
  await localDb(params.craftspace_id).run(
    `INSERT INTO cms_data (id, project_id, name, description, original_filename,
       json_file_path, field_structure, row_count, field_count, import_errors, file_size,
       created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      params.project_id,
      params.name,
      params.description ?? null,
      params.original_filename ?? null,
      params.json_file_path ?? null,
      params.field_structure ? JSON.stringify(params.field_structure) : null,
      params.row_count ?? null,
      params.field_count ?? null,
      params.import_errors ? JSON.stringify(params.import_errors) : null,
      params.file_size ?? null,
      ts,
      ts,
    ],
  );
  invalidate("cms_data");
  return id;
}

export async function updateCmsData(params: {
  craftspace_id: string;
  id: string;
  name?: string;
  description?: string;
  original_filename?: string;
  json_file_path?: string;
  field_structure?: unknown;
  row_count?: number;
  field_count?: number;
  import_errors?: unknown[];
  file_size?: number;
}): Promise<void> {
  const { id, craftspace_id, ...fields } = params;
  const setClauses: string[] = ["updated_at = ?"];
  const values: unknown[] = [now()];

  if (fields.name !== undefined) { setClauses.push("name = ?"); values.push(fields.name); }
  if (fields.description !== undefined) { setClauses.push("description = ?"); values.push(fields.description); }
  if (fields.original_filename !== undefined) { setClauses.push("original_filename = ?"); values.push(fields.original_filename); }
  if (fields.json_file_path !== undefined) { setClauses.push("json_file_path = ?"); values.push(fields.json_file_path); }
  if (fields.field_structure !== undefined) { setClauses.push("field_structure = ?"); values.push(JSON.stringify(fields.field_structure)); }
  if (fields.row_count !== undefined) { setClauses.push("row_count = ?"); values.push(fields.row_count); }
  if (fields.field_count !== undefined) { setClauses.push("field_count = ?"); values.push(fields.field_count); }
  if (fields.import_errors !== undefined) { setClauses.push("import_errors = ?"); values.push(JSON.stringify(fields.import_errors)); }
  if (fields.file_size !== undefined) { setClauses.push("file_size = ?"); values.push(fields.file_size); }

  values.push(id);
  await localDb(craftspace_id).run(`UPDATE cms_data SET ${setClauses.join(", ")} WHERE id = ?`, values);
  invalidate("cms_data");
}

export async function removeCmsData(id: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).run("DELETE FROM cms_data WHERE id = ?", [id]);
  invalidate("cms_data");
}

// ─── cms_records functions ───────────────────────────────────────────────────

export interface LocalCmsRecord {
  id: string;
  cms_data_id: string;
  project_id: string;
  data: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export async function bulkReplaceCmsRecords(
  cmsDataId: string,
  projectId: string,
  rows: Record<string, unknown>[],
  craftspaceId: string,
): Promise<void> {
  const ts = now();
  const operations = [
    { sql: "DELETE FROM cms_records WHERE cms_data_id = ?", params: [cmsDataId] },
    ...rows.map((row, i) => ({
      sql: `INSERT INTO cms_records (id, cms_data_id, project_id, data, created_at, updated_at)
            VALUES (?, ?, ?, ?, ?, ?)`,
      params: [`${cmsDataId}_${i}`, cmsDataId, projectId, JSON.stringify(row), ts, ts],
    })),
  ];

  await localDb(craftspaceId).transaction(operations);
  invalidate("cms_records");
}

export async function getCmsRecordsByCmsData(cmsDataId: string, craftspaceId: string): Promise<LocalCmsRecord[]> {
  const rows = await localDb(craftspaceId).query<{
    id: string;
    cms_data_id: string;
    project_id: string;
    data: string;
    created_at?: string | null;
    updated_at?: string | null;
  }>(
    "SELECT id, cms_data_id, project_id, data, created_at, updated_at FROM cms_records WHERE cms_data_id = ?",
    [cmsDataId],
  );

  return rows.map((row) => ({
    id: row.id,
    cms_data_id: row.cms_data_id,
    project_id: row.project_id,
    data: JSON.parse(row.data || "{}") as Record<string, unknown>,
    created_at: row.created_at ?? undefined,
    updated_at: row.updated_at ?? undefined,
  }));
}

export async function getAllCmsRecordsByProject(projectId: string, craftspaceId: string): Promise<LocalCmsRecord[]> {
  const rows = await localDb(craftspaceId).query<{
    id: string;
    cms_data_id: string;
    project_id: string;
    data: string;
    created_at?: string | null;
    updated_at?: string | null;
  }>(
    "SELECT id, cms_data_id, project_id, data, created_at, updated_at FROM cms_records WHERE project_id = ?",
    [projectId],
  );

  return rows.map((row) => ({
    id: row.id,
    cms_data_id: row.cms_data_id,
    project_id: row.project_id,
    data: JSON.parse(row.data || "{}") as Record<string, unknown>,
    created_at: row.created_at ?? undefined,
    updated_at: row.updated_at ?? undefined,
  }));
}

export async function updateLocalCmsRecord(
  id: string,
  data: Record<string, unknown>,
  craftspaceId: string,
): Promise<void> {
  await localDb(craftspaceId).run(
    "UPDATE cms_records SET data = ?, updated_at = ? WHERE id = ?",
    [JSON.stringify(data), now(), id],
  );
  invalidate("cms_records");
}

export async function deleteLocalCmsRecord(id: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).run("DELETE FROM cms_records WHERE id = ?", [id]);
  invalidate("cms_records");
}

export async function deleteRecordsByCmsDataId(cmsDataId: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).run("DELETE FROM cms_records WHERE cms_data_id = ?", [cmsDataId]);
  invalidate("cms_records");
}
